import React, { useState, useEffect, useCallback } from "react";
import { Outlet, Link } from "react-router-dom";
import { blogService } from "../services/blogService";
import { useTranslation } from "../hooks/useTranslation";

// Support both CRA and Vite environment variables
const getApiUrl = () => {
  // Try Vite environment variables first
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_REACT_APP_PUBLIC_URL) {
    return import.meta.env.VITE_REACT_APP_PUBLIC_URL.replace(/\/$/, '');
  }
  // Default fallback
  return 'https://itcsante-backend.onrender.com';
};

const API_URL = getApiUrl();

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      
      // Use shared blog service - this ensures consistency with admin panel
      const publishedBlogs = await blogService.getPublishedBlogs();
      console.log('Fetched blogs:', publishedBlogs); // Debug log
      setBlogs(publishedBlogs);
    } catch (error) {
      console.error('Blog fetch error:', error);
      setError(t('blog.errorLoading', `Error loading blogs: ${error.message}`));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-pulse space-y-4">
        <div className="h-12 w-12 bg-green-200 rounded-full mx-auto"></div>
        <div className="h-4 w-48 bg-green-100 rounded mx-auto"></div>
      </div>
    </div>
  );

  if (error)
    return (
      <div className="w-full min-h-screen flex justify-center items-center">
        {t('common.error')}: {error}
      </div>
    );

  return (
    <div className="w-full px-3 md:px-12 pb-12 min-h-full pt-[8rem]">
      {/* Header with refresh button */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Our Blog</h1>
        <button
          onClick={fetchBlogs}
          disabled={loading}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
        >
          <svg
            className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div
        className={`w-full ${
          blogs.length === 0
            ? "flex flex-col justify-center items-center"
            : "gap-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
        }`}
      >
      {blogs.length === 0 ? (
        <div className="text-center w-full min-h-12 flex flex-col justify-center items-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No blogs found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating your first blog post.
          </p>
        </div>
      ) : (
        blogs.map((blog) => <BlogWithLike key={blog._id} blog={blog} />)
      )}
      </div>
      <Outlet />
    </div>
  );
};

const BlogWithLike = ({ blog }) => {
  const [likeCount, setLikeCount] = useState(blog.likes ?? 0);

  const handleLike = () => {
    setLikeCount((count) => count + 1);
  };

  // Ensure image URL works on phone + pc
  const getImageUrl = (photo) => {
    if (!photo) return "https://via.placeholder.com/300x200?text=No+Image";
    // Handle base64 images, external URLs, and local paths
    if (photo.startsWith("data:image/")) return photo;
    if (photo.startsWith("http")) return photo;
    if (photo.startsWith("/")) return `${API_URL}${photo}`;
    return `${API_URL}/${photo}`;
  };

  return (
    <div className="relative border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow flex flex-col">
      <img
        src={getImageUrl(blog.imageUrl || blog.coverPhoto)}
        alt={blog.title}
        loading="lazy"
        decoding="async"
        onError={(e) =>
          (e.target.src =
            "https://via.placeholder.com/300x200?text=No+Image")
        }
        className="w-full h-48 object-cover rounded-md mb-4"
      />

      <Link 
        to={`/blog/${blog._id}`}
        className="text-lg font-semibold mb-2 truncate block hover:text-green-600 transition-colors cursor-pointer"
      >
        {blog.title}
      </Link>

      {/* Content preview - clickable */}
      <Link 
        to={`/blog/${blog._id}`}
        className="text-gray-600 text-sm mb-3 line-clamp-3 hover:text-green-600 transition-colors cursor-pointer"
      >
        {blog.content.length > 150 
          ? `${blog.content.substring(0, 150)}...` 
          : blog.content
        }
      </Link>

      <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
        <span>
          {new Date(blog.createdAt).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </span>
        <span>{blog.author}</span>
      </div>

      <div className="flex items-center space-x-4 mt-auto text-gray-700">
        <div
          className="flex items-center space-x-1"
          title={`${blog.views} views`}
        >
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
          <span>{blog.views}</span>
        </div>

        <button
          onClick={handleLike}
          aria-label="Like this post"
          className="flex items-center space-x-1 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500 rounded"
          title="Like"
          type="button"
        >
          <svg
            className="w-6 h-6 text-red-600"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
          <span>{likeCount}</span>
        </button>
      </div>
    </div>
  );
};

export default Blog;
